import React from 'react';
import {
  IonPage,
  IonContent,
  IonButton,
  IonRadioGroup,
  IonRadio,
  IonIcon,
  IonSpinner,
  useIonViewWillLeave,
} from '@ionic/react';
import { addIcons } from 'ionicons';
import {
  alertCircleOutline,
  chatbubbleEllipsesOutline,
  heartOutline,
} from 'ionicons/icons';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { type TemplateSet } from '@/modules/template';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import ShareWizardHeader from './ShareWizardHeader';
import AddressingModeSegment from './AddressingModeSegment';
import { pxToRem } from '@/modules/config/constants';

// ⚠️ Template icon registration — READ BEFORE ADDING A TEMPLATE ICON ⚠️
//
// Each tone card below renders `<IonIcon icon={tone.icon} />`, where
// `tone.icon` is a STRING name (e.g. "heart-outline") pulled from the
// template `icon:` fields in src/data/*.yaml. Ionicons can only render a
// string name if that name has been registered via addIcons(); otherwise
// it falls back to fetching the SVG by URL, which throws
// "Failed to construct 'URL': Invalid base URL" (Stencil's resource base
// URL isn't initialized yet) and spams the console with warnings.
//
// THEREFORE: whenever you add a new `icon:` value to any of the
// src/data/*_templates.yaml files, you MUST add the matching ionicons
// import + kebab-case registration entry below. The keys here must exactly
// match the strings used in the YAML.
addIcons({
  'alert-circle-outline': alertCircleOutline,
  'chatbubble-ellipses-outline': chatbubbleEllipsesOutline,
  'heart-outline': heartOutline,
});

const StyleStep: React.FC = () => {
  const {
    topic,
    isTopicLoading,
    topicError,
    selectedToneId,
    setSelectedToneId,
    singletonMode,
    setSingletonMode,
    recipientCountsByIntent,
    templateSets,
    inProgressLegId,
    nav,
  } = useShareWizardContext();

  // With one tone there is nothing to pick, so the list is hidden and Next is
  // free. The step still
  // earns its place: it owns the individual-vs-group send choice above.
  const hasToneChoice = templateSets.length > 1;

  const canAdvance = selectedToneId !== null || !hasToneChoice;

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  return (
    <IonPage>
      <ShareWizardHeader
        topicTitle={topic?.title ?? ''}
        title="Style"
        progress={nav.progress}
        onBack={nav.goBack}
      />
      <IonContent style={{ '--background': 'var(--app-page-background)' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            // Bottom clearance so the last tone card clears the fixed FooterDock.
            padding: '16px 20px 120px',
          }}
        >
          {isTopicLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <IonSpinner />
            </div>
          ) : topicError ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <p style={{ color: '#c33' }}>{topicError}</p>
              <IonButton onClick={nav.exit} fill="clear">
                Close
              </IonButton>
            </div>
          ) : (
            <>
              <AddressingModeSegment
                mode={singletonMode ? 'dm' : 'group-text'}
                onChange={mode => setSingletonMode(mode === 'dm')}
                recipientCountsByIntent={recipientCountsByIntent}
                disabled={inProgressLegId != null}
              />

              {hasToneChoice && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <p
                    style={{
                      color: '#666',
                      fontSize: pxToRem(14),
                      margin: '0 0 4px 0',
                    }}
                  >
                    How do you want to come across?
                  </p>
                  <IonRadioGroup
                    value={selectedToneId}
                    onIonChange={e => setSelectedToneId(e.detail.value)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    {templateSets.map(tone => (
                      <ToneCard
                        key={tone.id}
                        tone={tone}
                        selected={tone.id === selectedToneId}
                      />
                    ))}
                  </IonRadioGroup>
                </div>
              )}
            </>
          )}
        </div>
      </IonContent>
      {!isTopicLoading && !topicError && (
        <FooterDock background="transparent">
          <FooterDockRow landmark>
            <SmartCaseButton
              expand="block"
              color="primary"
              data-testid="share-wizard-next"
              disabled={!canAdvance || inProgressLegId != null}
              onClick={nav.goToFirstCompose}
              style={{ margin: 0 }}
            >
              Next
            </SmartCaseButton>
          </FooterDockRow>
        </FooterDock>
      )}
    </IonPage>
  );
};

interface ToneCardProps {
  tone: TemplateSet;
  selected: boolean;
}

// A tone row is a native IonRadio (platform-standard indicator: iOS checkmark,
// Material dot) with its default placement — label at the start, indicator at
// the end. The card highlight is driven off the group's selection.
const ToneCard: React.FC<ToneCardProps> = ({ tone, selected }) => {
  return (
    <IonRadio
      value={tone.id}
      data-testid={`share-tone-card-${tone.id}`}
      style={{
        width: '100%',
        padding: '12px 14px',
        // Constant border width so selection never changes the box height (a
        // 0.5px→1.5px swap shifted the label up a pixel). The selected accent
        // comes from color + an inset ring drawn with box-shadow, which doesn't
        // affect layout.
        border: `0.5px solid ${selected ? '#D85A30' : '#ddd'}`,
        boxShadow: selected ? 'inset 0 0 0 1px #D85A30' : undefined,
        borderRadius: '12px',
        background: selected ? '#FAECE7' : 'transparent',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {tone.icon && (
          <IonIcon
            icon={tone.icon}
            style={{ fontSize: pxToRem(24), color: '#993C1D', flexShrink: 0 }}
          />
        )}
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: pxToRem(14),
            fontWeight: 500,
            color: '#222',
          }}
        >
          {tone.name}
        </span>
      </span>
    </IonRadio>
  );
};

export default StyleStep;
